/**
 * Widget K: Retirement Plan Type Spread
 * 
 * Displays a donut chart showing the distribution of clients by
 * retirement plan type: FERS, CSRS, Private plan, No plan, Unknown.
 * Tooltips show FERS subtype breakdown when available.
 */

import React, { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculatePlanTypeSpread, PlanTypeSpreadData } from "../utils/widgetCalculations";
import { getUnifiedChartColor } from "./widgetColors";
import { ChartTooltip, CHART_MARGINS, CHART_ANIMATION, renderPieLabel } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";
import { isDataEmpty, getSampleData } from "../utils/sampleData";
import { Badge } from "../../ui/badge";

interface PlanTypeSpreadWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const PLAN_TYPE_LABELS = {
  FERS: "FERS",
  CSRS: "CSRS",
  Private: "Private Plan",
  NoPlan: "No Plan",
  Unknown: "Unknown",
};

export function PlanTypeSpreadWidget({
  clients,
  isLoading = false,
  userId,
}: PlanTypeSpreadWidgetProps) {
  const api = XanoAPI.getInstance();

  // Fetch data from API endpoint
  const { data: apiData, isLoading: apiLoading, error: apiError } = useQuery({
    queryKey: ["plan-type-spread", userId],
    queryFn: async () => {
      const result = await api.getPlanTypeSpread(userId);
      console.log("🔍 PlanTypeSpreadWidget: API response:", result);
      console.log("🔍 PlanTypeSpreadWidget: API response type:", typeof result);
      console.log("🔍 PlanTypeSpreadWidget: Has categories?", !!result?.categories);
      if (result?.categories) {
        console.log("🔍 PlanTypeSpreadWidget: Categories:", JSON.stringify(result.categories, null, 2));
      }
      if (result?.fersSubtypes) {
        console.log("🔍 PlanTypeSpreadWidget: fersSubtypes:", JSON.stringify(result.fersSubtypes, null, 2));
      }
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Log any errors
  useEffect(() => {
    if (apiError) {
      console.error("❌ PlanTypeSpreadWidget: Query error:", apiError);
    }
  }, [apiError]);

  const data = useMemo(() => {
    let result: PlanTypeSpreadData;
    
    console.log("🔍 PlanTypeSpreadWidget: Processing data - apiData:", apiData);
    console.log("🔍 PlanTypeSpreadWidget: apiData is null?", apiData === null);
    console.log("🔍 PlanTypeSpreadWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 PlanTypeSpreadWidget: Clients available:", clients.length);
    
    let usingSampleData = false;
    
    // Check if API data has valid non-zero categories
    const hasValidApiData = apiData && 
      apiData.categories && 
      typeof apiData.categories === 'object' &&
      Object.keys(apiData.categories).length > 0;
    
    // Calculate sum of all category values to verify we have real data
    let apiDataSum = 0;
    if (apiData?.categories && typeof apiData.categories === 'object') {
      apiDataSum = Object.values(apiData.categories).reduce((sum: number, val: any) => {
        const numVal = typeof val === 'number' ? val : 0;
        return sum + numVal;
      }, 0);
    }
    
    console.log("🔍 PlanTypeSpreadWidget: hasValidApiData?", hasValidApiData);
    console.log("🔍 PlanTypeSpreadWidget: apiData exists?", !!apiData);
    console.log("🔍 PlanTypeSpreadWidget: categories exists?", !!apiData?.categories);
    console.log("🔍 PlanTypeSpreadWidget: categories keys length:", apiData?.categories ? Object.keys(apiData.categories).length : 0);
    console.log("🔍 PlanTypeSpreadWidget: apiDataSum (sum of all values):", apiDataSum);
    
    // Use API data if it exists and has non-zero values
    if (hasValidApiData && apiDataSum > 0) {
      // API data is available and valid - transform to match PlanTypeSpreadData format
      const totalClients = Object.values(apiData.categories).reduce((sum: number, val: any) => sum + (typeof val === 'number' ? val : 0), 0);
      console.log("✅ PlanTypeSpreadWidget: Using API data");
      console.log("🔍 PlanTypeSpreadWidget: Raw categories:", JSON.stringify(apiData.categories, null, 2));
      console.log("🔍 PlanTypeSpreadWidget: Total clients from API:", totalClients);
      
      const privateCount = apiData.categories.Private ?? (apiData.categories["401k"] ?? 0) + (apiData.categories["403b"] ?? 0);
      
      result = {
        categories: {
          FERS: apiData.categories.FERS ?? 0,
          CSRS: apiData.categories.CSRS ?? 0,
          Private: privateCount,
          NoPlan: apiData.categories.NoPlan ?? 0,
          Unknown: apiData.categories.Unknown ?? 0,
        },
        fersSubtypes: apiData.fersSubtypes?.FERS ? {
          RAE: apiData.fersSubtypes.FERS["FERS-RAE"] ?? 0,
          FRAE: apiData.fersSubtypes.FERS["FERS-FRAE"] ?? 0,
          Standard: apiData.fersSubtypes.FERS["FERS Basic"] ?? 0,
        } : undefined,
      };
      
      usingSampleData = false;
    } else if (clients.length > 0) {
      // API data is empty or unavailable - try calculating from clients
      console.log("⚠️ PlanTypeSpreadWidget: API data empty or unavailable, trying client calculation");
      try {
        const calculatedData = calculatePlanTypeSpread(clients);
        const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
        console.log("🔍 PlanTypeSpreadWidget: Calculated from clients - Total:", calculatedTotal);
        console.log("🔍 PlanTypeSpreadWidget: Calculated categories:", JSON.stringify(calculatedData.categories, null, 2));
        
        if (calculatedTotal > 0) {
          console.log("✅ PlanTypeSpreadWidget: Using calculated data from clients");
          result = calculatedData;
          usingSampleData = false;
        } else {
          console.log("⚠️ PlanTypeSpreadWidget: Calculated data also empty, using sample data");
          const sampleData = getSampleData.planTypeSpread();
          const privateCount = (sampleData.categories["401k"] || 0) + (sampleData.categories["403b"] || 0);
          result = {
            categories: {
              FERS: sampleData.categories.FERS || 35,
              CSRS: sampleData.categories.CSRS || 15,
              Private: privateCount || 25,
              NoPlan: 0,
            Unknown: 0,
          },
          fersSubtypes: sampleData.fersSubtypes?.FERS ? {
            RAE: sampleData.fersSubtypes.FERS["FERS-RAE"] ?? 10,
            FRAE: sampleData.fersSubtypes.FERS["FERS-FRAE"] ?? 5,
            Standard: sampleData.fersSubtypes.FERS["FERS Basic"] ?? 20,
          } : undefined,
        };
        
          usingSampleData = true;
        }
      } catch (error) {
        console.error("❌ PlanTypeSpreadWidget: Error calculating from clients:", error);
        const sampleData = getSampleData.planTypeSpread();
        const privateCount = (sampleData.categories["401k"] || 0) + (sampleData.categories["403b"] || 0);
        result = {
          categories: {
            FERS: sampleData.categories.FERS || 35,
            CSRS: sampleData.categories.CSRS || 15,
            Private: privateCount || 25,
            NoPlan: 0,
            Unknown: 0,
          },
          fersSubtypes: sampleData.fersSubtypes?.FERS ? {
            RAE: sampleData.fersSubtypes.FERS["FERS-RAE"] ?? 10,
            FRAE: sampleData.fersSubtypes.FERS["FERS-FRAE"] ?? 5,
            Standard: sampleData.fersSubtypes.FERS["FERS Basic"] ?? 20,
          } : undefined,
        };
        usingSampleData = true;
      }
    } else {
      // No clients available - use sample data
      console.log("⚠️ PlanTypeSpreadWidget: No clients available, using sample data");
      const sampleData = getSampleData.planTypeSpread();
      const privateCount = (sampleData.categories["401k"] || 0) + (sampleData.categories["403b"] || 0);
      result = {
        categories: {
          FERS: sampleData.categories.FERS || 35,
          CSRS: sampleData.categories.CSRS || 15,
          Private: privateCount || 25,
          NoPlan: 0,
          Unknown: 0,
        },
        fersSubtypes: sampleData.fersSubtypes?.FERS ? {
          RAE: sampleData.fersSubtypes.FERS["FERS-RAE"] ?? 10,
          FRAE: sampleData.fersSubtypes.FERS["FERS-FRAE"] ?? 5,
          Standard: sampleData.fersSubtypes.FERS["FERS Basic"] ?? 20,
        } : undefined,
      };
      usingSampleData = true;
    }
    
    // Transform to chart data format
    const totalClients = Object.values(result.categories).reduce((a: number, b: number) => a + b, 0);
    console.log("🔍 PlanTypeSpreadWidget: Total clients:", totalClients);
    console.log("🔍 PlanTypeSpreadWidget: Category entries:", Object.entries(result.categories));
    
    const chartData = Object.entries(result.categories)
      .filter(([_, count]) => count > 0)
      .map(([key, count], index) => ({
        name: PLAN_TYPE_LABELS[key as keyof typeof PLAN_TYPE_LABELS],
        value: count,
        percentage: totalClients > 0 ? (count / totalClients) * 100 : 0,
        fill: getUnifiedChartColor(index),
        key,
        fersSubtypes: key === "FERS" ? result.fersSubtypes : undefined,
      }));
    
    console.log("🔍 PlanTypeSpreadWidget: Chart data after filtering:", chartData);
    console.log("🔍 PlanTypeSpreadWidget: Chart data length:", chartData.length);

    return { chartData, totalClients, fersSubtypes: result.fersSubtypes, usingSampleData };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If we have no chart data, try to use sample data directly
  if (data.chartData.length === 0 && !data.usingSampleData) {
    console.log("⚠️ PlanTypeSpreadWidget: No chart data and not using sample data, forcing sample data");
    const sampleData = getSampleData.planTypeSpread();
    const privateCount = (sampleData.categories["401k"] || 0) + (sampleData.categories["403b"] || 0);
    const forcedResult = {
      categories: {
        FERS: sampleData.categories.FERS || 35,
        CSRS: sampleData.categories.CSRS || 15,
        Private: privateCount || 25,
        NoPlan: 0,
        Unknown: 0,
      },
    };
    const forcedTotal = Object.values(forcedResult.categories).reduce((a: number, b: number) => a + b, 0);
    const forcedChartData = Object.entries(forcedResult.categories)
      .filter(([_, count]) => count > 0)
      .map(([key, count], index) => ({
        name: PLAN_TYPE_LABELS[key as keyof typeof PLAN_TYPE_LABELS],
        value: count,
        percentage: forcedTotal > 0 ? (count / forcedTotal) * 100 : 0,
        fill: getUnifiedChartColor(index),
        key,
        fersSubtypes: key === "FERS" ? sampleData.fersSubtypes?.FERS ? {
          RAE: sampleData.fersSubtypes.FERS["FERS-RAE"] ?? 10,
          FRAE: sampleData.fersSubtypes.FERS["FERS-FRAE"] ?? 5,
          Standard: sampleData.fersSubtypes.FERS["FERS Basic"] ?? 20,
        } : undefined : undefined,
      }));
    
    if (forcedChartData.length > 0) {
      // Use forced data
      return (
        <Card>
          <CardContent>
            <div className="mb-2 flex justify-end">
              <Badge variant="secondary" className="text-xs">Sample Data</Badge>
            </div>
            <div className="flex flex-row items-center justify-between space-y-0 pb-2 mb-4">
              <h3 className="text-sm font-medium">Retirement Plan Type Spread</h3>
              {forcedTotal > 0 && (
                <span className="text-xs text-muted-foreground">({forcedTotal} clients)</span>
              )}
            </div>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={CHART_MARGINS}>
                  <Pie
                    data={forcedChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => renderPieLabel(entry, 5)}
                    outerRadius={95}
                    innerRadius={60}
                    paddingAngle={3}
                    dataKey="value"
                    {...CHART_ANIMATION}
                  >
                    {forcedChartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.fill}
                        stroke="hsl(var(--background))"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={<ChartTooltip 
                      formatter={(value) => {
                        const dataPoint = forcedChartData.find(d => d.value === value);
                        return [
                          `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                          dataPoint?.name || ""
                        ];
                      }}
                      labelFormatter={(label) => {
                        const dataPoint = forcedChartData.find(d => d.value === parseInt(label) || d.name === label);
                        if (dataPoint?.fersSubtypes) {
                          const subtypes = Object.entries(dataPoint.fersSubtypes)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(", ");
                          return `${dataPoint.name} - FERS Subtypes: ${subtypes}`;
                        }
                        return dataPoint?.name || label;
                      }}
                    />} 
                    cursor={{ fill: "transparent" }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={60}
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ paddingTop: "15px" }}
                    formatter={(value, entry: any) => {
                      const dataPoint = forcedChartData.find((d) => d.key === entry.payload?.key);
                      if (!dataPoint) return value;
                      return (
                        <span className="text-xs text-muted-foreground">
                          {value} <span className="font-semibold text-foreground">{dataPoint.value}</span> ({dataPoint.percentage.toFixed(1)}%)
                        </span>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap justify-between items-start gap-x-4 gap-y-3 pt-3 border-t">
              {forcedChartData.map((item) => (
                <div key={item.key} className="space-y-1 text-center flex-1 min-w-[80px]">
                  <div className="text-lg font-bold" style={{ color: item.fill }}>
                    {item.value}
                  </div>
                  <div className="text-xs font-medium text-foreground">{item.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.percentage.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }
  }
  
  // Only show empty state if we're not using sample data and have no data
  if (!data.usingSampleData && (data.totalClients === 0 || data.chartData.length === 0)) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No plan type data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // If using sample data but still no chart data, force use sample data again
  if (data.usingSampleData && data.chartData.length === 0) {
    console.error("❌ PlanTypeSpreadWidget: Using sample data but chartData is empty!");
    console.error("🔍 PlanTypeSpreadWidget: Sample data result:", data);
    console.error("🔍 PlanTypeSpreadWidget: Total clients:", data.totalClients);
    console.error("🔍 PlanTypeSpreadWidget: Chart data:", data.chartData);
    
    // Force use sample data directly
    const sampleData = getSampleData.planTypeSpread();
    const privateCount = (sampleData.categories["401k"] || 0) + (sampleData.categories["403b"] || 0);
    const forcedResult = {
      categories: {
        FERS: sampleData.categories.FERS || 35,
        CSRS: sampleData.categories.CSRS || 15,
        Private: privateCount || 25,
        NoPlan: 0,
        Unknown: 0,
      },
    };
    const forcedTotal = Object.values(forcedResult.categories).reduce((a: number, b: number) => a + b, 0);
    const forcedChartData = Object.entries(forcedResult.categories)
      .filter(([_, count]) => count > 0)
      .map(([key, count], index) => ({
        name: PLAN_TYPE_LABELS[key as keyof typeof PLAN_TYPE_LABELS],
        value: count,
        percentage: forcedTotal > 0 ? (count / forcedTotal) * 100 : 0,
        fill: getUnifiedChartColor(index),
        key,
        fersSubtypes: key === "FERS" ? sampleData.fersSubtypes?.FERS ? {
          RAE: sampleData.fersSubtypes.FERS["FERS-RAE"] ?? 0,
          FRAE: sampleData.fersSubtypes.FERS["FERS-FRAE"] ?? 0,
          Standard: sampleData.fersSubtypes.FERS["FERS Basic"] ?? 0,
        } : undefined : undefined,
      }));
    
    console.log("🔍 PlanTypeSpreadWidget: Forced chart data:", forcedChartData);
    
    // Use forced data if we have chart data
    if (forcedChartData.length > 0) {
      return (
        <Card>
          <CardContent>
            <div className="mb-2 flex justify-end">
              <Badge variant="secondary" className="text-xs">Sample Data</Badge>
            </div>
            <div className="flex flex-row items-center justify-between space-y-0 pb-2 mb-4">
              <h3 className="text-sm font-medium">Retirement Plan Type Spread</h3>
              {forcedTotal > 0 && (
                <span className="text-xs text-muted-foreground">({forcedTotal} clients)</span>
              )}
            </div>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={CHART_MARGINS}>
                  <Pie
                    data={forcedChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => renderPieLabel(entry, 5)}
                    outerRadius={95}
                    innerRadius={60}
                    paddingAngle={3}
                    dataKey="value"
                    {...CHART_ANIMATION}
                  >
                    {forcedChartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.fill}
                        stroke="hsl(var(--background))"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    content={<ChartTooltip 
                      formatter={(value) => {
                        const dataPoint = forcedChartData.find(d => d.value === value);
                        return [
                          `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                          dataPoint?.name || ""
                        ];
                      }}
                      labelFormatter={(label) => {
                        const dataPoint = forcedChartData.find(d => d.value === parseInt(label) || d.name === label);
                        if (dataPoint?.fersSubtypes) {
                          const subtypes = Object.entries(dataPoint.fersSubtypes)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(", ");
                          return `${dataPoint.name} - FERS Subtypes: ${subtypes}`;
                        }
                        return dataPoint?.name || label;
                      }}
                    />} 
                    cursor={{ fill: "transparent" }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={60}
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ paddingTop: "15px" }}
                    formatter={(value, entry: any) => {
                      const dataPoint = forcedChartData.find((d) => d.key === entry.payload?.key);
                      if (!dataPoint) return value;
                      return (
                        <span className="text-xs text-muted-foreground">
                          {value} <span className="font-semibold text-foreground">{dataPoint.value}</span> ({dataPoint.percentage.toFixed(1)}%)
                        </span>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap justify-between items-start gap-x-4 gap-y-3 pt-3 border-t">
              {forcedChartData.map((item) => (
                <div key={item.key} className="space-y-1 text-center flex-1 min-w-[80px]">
                  <div className="text-lg font-bold" style={{ color: item.fill }}>
                    {item.value}
                  </div>
                  <div className="text-xs font-medium text-foreground">{item.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.percentage.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }
  }

  return (
    <Card>
      <CardContent>
        {data.usingSampleData && (
          <div className="mb-2 flex justify-end">
            <Badge variant="secondary" className="text-xs">Sample Data</Badge>
          </div>
        )}
        <div className="flex flex-row items-center justify-between space-y-0 pb-2 mb-4">
          <h3 className="text-sm font-medium">Retirement Plan Type Spread</h3>
          {data.totalClients > 0 && (
            <span className="text-xs text-muted-foreground">({data.totalClients} clients)</span>
          )}
        </div>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={CHART_MARGINS}>
              <Pie
                data={data.chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => renderPieLabel(entry, 5)}
                outerRadius={95}
                innerRadius={60}
                paddingAngle={3}
                dataKey="value"
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.fill}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value) => {
                    const dataPoint = data.chartData.find(d => d.value === value);
                    return [
                      `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                      dataPoint?.name || ""
                    ];
                  }}
                  labelFormatter={(label) => {
                    const dataPoint = data.chartData.find(d => d.value === parseInt(label) || d.name === label);
                    if (dataPoint?.fersSubtypes) {
                      const subtypes = Object.entries(dataPoint.fersSubtypes)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ");
                      return `${dataPoint.name} - FERS Subtypes: ${subtypes}`;
                    }
                    return dataPoint?.name || label;
                  }}
                />} 
                cursor={{ fill: "transparent" }}
              />
              <Legend
                verticalAlign="bottom"
                height={60}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ paddingTop: "15px" }}
                formatter={(value, entry: any) => {
                  const dataPoint = data.chartData.find((d) => d.key === entry.payload?.key);
                  if (!dataPoint) return value;
                  return (
                    <span className="text-xs text-muted-foreground">
                      {value} <span className="font-semibold text-foreground">{dataPoint.value}</span> ({dataPoint.percentage.toFixed(1)}%)
                    </span>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap justify-between items-start gap-x-4 gap-y-3 pt-3 border-t">
          {data.chartData.map((item) => (
            <div key={item.key} className="space-y-1 text-center flex-1 min-w-[80px]">
              <div className="text-lg font-bold" style={{ color: item.fill }}>
                {item.value}
              </div>
              <div className="text-xs font-medium text-foreground">{item.name}</div>
              <div className="text-xs text-muted-foreground">
                {item.percentage.toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

